import { useTranslation } from '../i18n';
import { LINKS } from '../brand';

/**
 * The three cross-platform CTA pills. In the unified SPA, the catalog/articles/studio surfaces
 * are same-origin (no subdomain hop), so `/`, `/articles`, and `/studio` are simple local paths.
 * Pack remains an off-platform GitHub link (the Pack lives in the gposingway/gposingway repo).
 *
 * Hidden on screens narrower than md, where the navbar hamburger takes over (see
 * EmporiumNav) and the same pills render stacked via the `mobile` variant below.
 */
export type PlatformSection = 'emporium' | 'articles' | 'studio' | 'pack';

export function PlatformPills({
  current,
  mobile = false,
}: {
  current?: PlatformSection;
  mobile?: boolean;
}) {
  const t = useTranslation();

  const pills = (
    <>
      <NavbarPill href="/articles" current={current === 'articles'} mobile={mobile}>
        {t('landing.navArticles')}
      </NavbarPill>
      <NavbarPill
        href={LINKS.github.pack}
        current={current === 'pack'}
        external
        mobile={mobile}
      >
        {t('landing.navPack')}
      </NavbarPill>
      <NavbarPill href="/studio" current={current === 'studio'} mobile={mobile}>
        {t('landing.navStudio')}
      </NavbarPill>
      <NavbarPill href="/emporium" current={current === 'emporium'} mobile={mobile}>
        {t('landing.navBrowse')}
      </NavbarPill>
    </>
  );

  if (mobile) {
    return <div class="flex flex-col gap-2">{pills}</div>;
  }

  return <div class="hidden items-center justify-center gap-2.5 md:flex">{pills}</div>;
}

/**
 * Pill-style CTA link. Local re-implementation so PlatformPills can stand alone without the
 * full shared Navbar component being imported.
 */
function NavbarPill({
  href,
  current,
  external,
  mobile,
  children,
}: {
  href: string;
  current?: boolean;
  external?: boolean;
  mobile?: boolean;
  children: import('preact').ComponentChildren;
}) {
  return (
    <a
      href={href}
      aria-current={current ? 'page' : undefined}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer noopener' : undefined}
      class={`rounded-full border text-[13px] transition-colors ${
        mobile ? 'block px-4 py-2 text-center' : 'px-3 py-1.5'
      } ${
        current
          ? 'border-accent bg-accent-glow text-accent'
          : 'border-rule text-fg-primary hover:border-accent-soft hover:bg-accent-glow'
      }`}
    >
      {children}
    </a>
  );
}
